import { useEffect, useState, type SyntheticEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { Brand } from "@shared/types";
import { buildBrandSeoContent } from "@shared/seoContent";
import { api } from "../lib/api";
import { TagPill } from "../components/TagPill";
import { SaveBrandButton } from "../components/SaveBrandButton";
import { CrowdSignalBar } from "../components/CrowdSignalBar";
import { EmptyState } from "../components/EmptyState";
import { scoreLabel, priceLevelLabel } from "../lib/formatting";
import { classifyLoadedBrandImage, looksLikeLogoUrl } from "../lib/imageHeuristics";
import { safeHttpUrl } from "../lib/safeUrl";

export function BrandProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "copied">("idle");

  useEffect(() => {
    if (!slug) return;
    api.brands.get(slug)
      .then((r) => setBrand(r.brand))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container" style={{ paddingBlock: "3rem" }}>
        <EmptyState
          title="Brand not on the radar"
          actions={
            <>
            <Link to="/brands"><button className="primary">Browse brands</button></Link>
            <Link to="/submit"><button>Submit a brand</button></Link>
            </>
          }
        >
          That brand is not live, or it has been removed from the public feed.
        </EmptyState>
      </div>
    );
  }
  if (!brand) return <div className="container empty-state">Loading…</div>;

  const tagsByType = (t: Brand["tags"][number]["type"]) => brand.tags.filter((x) => x.type === t);
  const seoContent = buildBrandSeoContent(brand);
  const verdict = buildRadarVerdict(brand);
  const websiteUrl = safeHttpUrl(brand.websiteUrl);
  const instagramUrl = safeInstagramUrl(brand.instagramHandle);

  const shareBrand = async () => {
    const url = window.location.href;
    const text = `${brand.name} is on the BeforeMainstream radar.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${brand.name} on BeforeMainstream`, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareState("copied");
        window.setTimeout(() => setShareState("idle"), 1800);
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        setShareState("copied");
        window.setTimeout(() => setShareState("idle"), 1800);
      } catch {
        setShareState("idle");
      }
    }
  };

  return (
    <article className="brand-profile container">
      <header className="brand-profile__hero">
        <div className="brand-profile__header">
          <div style={{ fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--fg-subtle)" }}>
            {brand.primaryCategory?.name ?? "Brand"} · {brand.country ?? brand.region ?? "—"}
          </div>
          <h1>{brand.name}</h1>
          {brand.shortSummary && <p className="brand-profile__summary">{brand.shortSummary}</p>}
          <section className="brand-radar-verdict" aria-label={`${brand.name} radar verdict`}>
            <div>
              <span>Radar verdict</span>
              <b>{verdict.label}</b>
              <p>{verdict.reason}</p>
            </div>
            <button type="button" onClick={shareBrand}>
              {shareState === "copied" ? "Link copied" : "Share this find"}
            </button>
          </section>
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <SaveBrandButton brandId={brand.id} />
            {websiteUrl && (
              <a href={websiteUrl} target="_blank" rel="noreferrer">
                <button>Visit website ↗</button>
              </a>
            )}
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noreferrer">
                <button>Instagram ↗</button>
              </a>
            )}
          </div>
        </div>
        <BrandProfileVisual brand={brand} />
      </header>

      <CrowdSignalBar brandId={brand.id} initial={brand.crowdSignals} />

      <section className="signal-cards">
        <SignalCard label="Emerging signal"   value={scoreLabel(brand.scores.discovery, "discovery")} />
        <SignalCard label="Mainstream"        value={scoreLabel(brand.scores.mainstream, "mainstream")} />
        <SignalCard label="Momentum"          value={scoreLabel(brand.scores.momentum, "momentum")} />
        <SignalCard label="Aesthetic clarity" value={scoreLabel(brand.scores.aestheticStrength, "aesthetic")} />
        <SignalCard label="Data confidence"   value={scoreLabel(brand.scores.confidence, "confidence")} />
        <SignalCard label="Price level"       value={priceLevelLabel(brand.priceLevel)} />
      </section>

      {brand.longSummary && (
        <div className="why-block">
          {brand.longSummary}
        </div>
      )}

      <section className="brand-seo-depth" aria-label={`${brand.name} discovery notes`}>
        <div className="brand-seo-depth__main">
          <span className="meta">Radar notes</span>
          <h2>Why {brand.name} is on the radar</h2>
          <p>{seoContent.intro}</p>
          <p>{seoContent.whyEarly}</p>
          <p>{seoContent.youthRelevance}</p>
        </div>
        <aside className="brand-seo-depth__evidence">
          <span>Discovery evidence</span>
          <ul>
            {seoContent.discoveryEvidence.map((item) => <li key={item}>{item}</li>)}
          </ul>
          {seoContent.radarAngles.length > 0 && (
            <>
              <span style={{ marginTop: "1rem" }}>Radar angles</span>
              <ul>
                {seoContent.radarAngles.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </>
          )}
        </aside>
      </section>

      {seoContent.relatedSearchTerms.length > 0 && (
        <section className="brand-related-terms" aria-label={`${brand.name} related search terms`}>
          <h3>Related signals</h3>
          <div>
            {seoContent.relatedSearchTerms.map((term) => <span key={term}>{term}</span>)}
          </div>
        </section>
      )}

      <TagsBlock label="Product categories" tags={[...tagsByType("primary_category"), ...tagsByType("secondary_category")]} />
      <TagsBlock label="Style directions"   tags={tagsByType("style_direction")} />
      <TagsBlock label="Aesthetic"          tags={tagsByType("aesthetic")} />
      <TagsBlock label="Materials"          tags={tagsByType("material")} />
      <TagsBlock label="Market"             tags={tagsByType("market")} />

      <section style={{ marginTop: "3rem", color: "var(--fg-subtle)", fontSize: "0.85rem" }}>
        <div>Found through: discovery pipeline</div>
        {brand.lastCheckedAt && <div>Last checked: {new Date(brand.lastCheckedAt).toLocaleDateString()}</div>}
        <div style={{ marginTop: "1rem" }}>
          <a href={`mailto:hello@px7.digital?subject=Correction for ${brand.name}`}>Report a correction</a>
        </div>
      </section>
    </article>
  );
}

function safeInstagramUrl(handle: string | null): string | null {
  if (!handle) return null;
  const clean = handle.replace(/^@+/, "").trim();
  return /^[a-zA-Z0-9._]{1,30}$/.test(clean) ? `https://instagram.com/${clean}` : null;
}

function buildRadarVerdict(brand: Brand): { label: string; reason: string } {
  const discovery = brand.scores.discovery ?? 0;
  const mainstream = brand.scores.mainstream ?? 100;
  const momentum = brand.scores.momentum ?? 0;
  const style = brand.tags.find((tag) => tag.type === "style_direction")?.name
    ?? brand.tags.find((tag) => tag.type === "aesthetic")?.name
    ?? brand.primaryCategory?.name
    ?? "style";
  const crowd = brand.crowdSignals?.counts;
  const positiveCrowd = (crowd?.early ?? 0) + (crowd?.would_wear ?? 0);

  if (discovery >= 75 && mainstream <= 45) {
    return {
      label: "Early heat",
      reason: `${brand.name} has a strong discovery signal while still reading low-mainstream, especially around ${style.toLowerCase()}.`,
    };
  }
  if (momentum >= 65 && mainstream <= 65) {
    return {
      label: "Momentum watch",
      reason: `${brand.name} is showing movement before it becomes obvious, with ${style.toLowerCase()} as the clearest signal.`,
    };
  }
  if (positiveCrowd > 0) {
    return {
      label: "Crowd signal",
      reason: `${positiveCrowd} early/wear vote${positiveCrowd === 1 ? "" : "s"} already point toward this being worth watching.`,
    };
  }
  return {
    label: "On the radar",
    reason: `${brand.name} is tracked here because its discovery, aesthetic, and mainstream signals still leave room before broader visibility.`,
  };
}

function BrandProfileVisual({ brand }: { brand: Brand }) {
  const imageUrl = normalizeImageUrl(brand.ogImageUrl);
  const [imageState, setImageState] = useState<"loading" | "photo" | "logo" | "failed">(
    imageUrl ? (looksLikeLogoUrl(imageUrl) ? "logo" : "loading") : "failed",
  );

  useEffect(() => {
    setImageState(imageUrl ? (looksLikeLogoUrl(imageUrl) ? "logo" : "loading") : "failed");
  }, [imageUrl]);

  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    setImageState(classifyLoadedBrandImage(e.currentTarget));
  };

  const className =
    imageState === "failed" ? "brand-profile__visual brand-profile__visual--text"
    : imageState === "logo" ? "brand-profile__visual brand-profile__visual--logo"
    : "brand-profile__visual";

  return (
    <div className={className}>
      {imageState === "failed" || !imageUrl
        ? <span>{brand.name}</span>
        : <img
            src={imageUrl}
            alt={`${brand.name} visual`}
            referrerPolicy="no-referrer"
            onLoad={onLoad}
            onError={() => setImageState("failed")}
          />
      }
    </div>
  );
}

function normalizeImageUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith("http://") ? `https://${url.slice("http://".length)}` : url;
}

function SignalCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="signal-card">
      <div className="signal-card__label">{label}</div>
      <div className="signal-card__value">{value}</div>
    </div>
  );
}

function TagsBlock({ label, tags }: { label: string; tags: Brand["tags"] }) {
  if (tags.length === 0) return null;
  return (
    <section style={{ marginTop: "2rem" }}>
      <h3 style={{ fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--fg-subtle)", marginBottom: "0.75rem" }}>{label}</h3>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
        {tags.map((t) => <TagPill key={t.id} tag={t} />)}
      </div>
    </section>
  );
}
